import Layout from 'Nori/Layout';
import Button from 'Nori/Button';
import SplitContent from 'Custom/Split/SplitContent';
import Hero from 'Custom/Hero';
import RichText from 'Custom/RichText';
import AjustableImage from 'Custom/AjustableImage';
import { Finder } from 'Custom/Finder';
import { HealthProvider } from 'Custom/HealthProvider';
import { REMOVE_UPSCRIPT_JOB_CODE } from 'Custom/Footer/Footer.utils.js';

import styles from 'styles/pages/howDoesNexplanonWorks.module.scss';

import PLUS_IMAGE from 'Images/misc/plus-gold.webp';
import UTERUS_IMAGE from 'Images/misc/uterus-image.webp';
import EFFECTIVE_IMAGE from 'Images/misc/effective.webp';
import ARM_IMAGE from 'Images/misc/arm-flipped.webp';

const HERO_IMAGE = {
  src: UTERUS_IMAGE,
  alt: "Diagram Showing How NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Works in a Woman's Reproductive System",
};

const HERO_IMAGE_MOBILE = {
  src: UTERUS_IMAGE,
  alt: "Diagram Showing How NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Works in a Woman's Reproductive System",
};

const HEAD = {
  title: 'How Does NEXPLANON® (etonogestrel implant) 68 mg Radiopaque Work?',
  description:
    'Discover how NEXPLANON® (etonogestrel implant) 68 mg Radiopaque works in your body. If the implant may be right for you, use the online tool to find a trained healthcare professional.',
  keywords: 'how does nexplanon work, how nexplanon works',
};

export default function HowDoesNexplanonWorks() {
  return (
    <Layout head={HEAD}>
      <Hero
        title={
          <>
            HOW <strong>NEXPLANON</strong> WORKS
          </>
        }
        background="primaryLight"
        variant="overlap"
        image={HERO_IMAGE}
        mobileImage={HERO_IMAGE_MOBILE}
        imageBreakpoint="xl"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Hero"
        data-design-module_name="HOW NEXPLANON WORKS"
        data-design-module_no="01"
      >
        <RichText className={styles.heroCopy}>
          <h2>NEXPLANON works to prevent pregnancy in several ways.</h2>
          <p>
            Once it gets inserted, it releases a steady, low dose of progestin,
            a hormone that works to prevent pregnancy in 3 ways:
          </p>
          <ul className={`primary-list`}>
            <li className="graphic-red">
              <strong>Stops ovulation</strong> by preventing the release of an
              egg from the ovary.
            </li>
            <li className="graphic-blue">
              <strong>Thins the lining of your uterus</strong>, which makes
              implantation of the egg more difficult.
            </li>
            <li className="graphic-yellow">
              <strong>Thickens mucus in the cervix</strong>, which makes it
              harder for the sperm to reach the egg.
            </li>
          </ul>
        </RichText>
      </Hero>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="NEXPLANON is just as effective as the pill - without the daily hassle"
        data-design-module_no="02"
      >
        <AjustableImage
          size="xl"
          background="secondary2Light"
          image={{
            src: EFFECTIVE_IMAGE,
            alt: 'Over 99% Effective: Less Than 1 Pregnancy per 100 Women Who Used NEXPLANON® (etonogestrel implant) 68 mg Radiopaque for 1 Year',
          }}
          specialImage={true}
        />
        <>
          <RichText>
            <h2>
              NEXPLANON is just as effective as the pill - without the daily
              hassle
            </h2>
            <p className="intro">
              This is birth control you don’t have to take every day. The small,
              flexible implant prevents pregnancy for up to 3 years.*
            </p>
            <p>*NEXPLANON must be removed by the end of the third year.</p>
          </RichText>
          <Button
            type="secondary"
            href="/effectiveness/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Get the Details"
            data-design-nori_id="Inline Content"
            data-design-module_name="NEXPLANON is just as effective as the pill - without the daily hassle"
            data-design-module_no="02"
          >
            Get the Details
          </Button>
        </>
      </SplitContent>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="NEXPLANON is inserted during an outpatient office visit"
        data-design-module_no="03"
      >
        <AjustableImage
          background="secondary4Light"
          image={{
            src: ARM_IMAGE,
            alt: 'Visual of Inner Arm Showcasing Position of Implant Insertion',
          }}
        />
        <>
          <RichText>
            <h2>NEXPLANON is inserted during an outpatient office visit</h2>
            <p className="intro">
              Typical insertion takes a few minutes and is performed under a
              local anesthetic. In clinical studies, 5.2% of patients felt
              insertion site pain. NEXPLANON can be removed at any time during
              the 3-year period.
            </p>
          </RichText>
          <Button
            type="secondary4"
            href="/insertion/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Learn More About Insertion"
            data-design-nori_id="Inline Content"
            data-design-module_name="NEXPLANON is inserted during an outpatient office visit"
            data-design-module_no="03"
          >
            Learn More About Insertion
          </Button>
        </>
      </SplitContent>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="side effects of NEXPLANON"
        data-design-module_no="04"
      >
        <AjustableImage image={{ src: PLUS_IMAGE }} />
        <>
          <RichText>
            <h2>side effects of NEXPLANON</h2>
            <p className="intro">
              The most common side effect of NEXPLANON is a change in your
              normal menstrual bleeding pattern. In studies, 1 in 10 women
              stopped using NEXPLANON because of an unfavorable change in their
              bleeding pattern. Besides changes in menstrual bleeding patterns,
              other frequent side effects that caused women to stop using the
              implant include mood swings, weight gain, headache, acne, and
              depressed mood.
            </p>
          </RichText>
          <Button
            href="/side-effects/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Side Effects of NEXPLANON"
            data-design-nori_id="Inline Content"
            data-design-module_name="side effects of NEXPLANON"
            data-design-module_no="04"
          >
            Side Effects of NEXPLANON
          </Button>
        </>
      </SplitContent>
      <Finder data-design-module_no="05" />
    </Layout>
  );
}
